import { useEffect, useLayoutEffect, useState } from 'react'
import { styled } from 'baseui'
import { useTranslation } from 'react-i18next'
import { lightTheme } from '@/customTheme'
import Badge from '@/components/Badge'
import DefaultButton from '@/components/DefaultButton'
import ModalWrapper from '@/components/Modal/ModalWrapper'
import { COMPACT_MODAL_MEDIA } from '@/components/Modal/ResponsiveModalLayout'
import PlanComparisonTable, { PlanComparisonRow } from '@/components/PlanComparisonTable'
import Stack from '@/components/Stack'
import Text from '@/components/Text'
import useModal from '@/hooks/useModal'
import { ShowModalType } from '@/scenes/engine/common/constants'
import { SignInManager } from '@/scenes/Editor/components/Navbar/components/SignInManager'
import TryPremiumSuccess from '@/scenes/Editor/components/Navbar/components/SignIn/TryPremiumSuccess'
import {
  formatPlanPrice,
  getFallbackSuperPlanDetail,
} from '@/scenes/Editor/components/Navbar/components/SignIn/Metsikon/fallbackPlanDetail'
import { LIMITED_TIME_OFFER_SOURCE } from '@/scenes/Editor/components/Navbar/components/SignIn/Metsikon/metsikonPlanConfig'
import { customAmplitude } from '@/utils/customAmplitude'
import { beginLimitedTimeOfferUpgrade, closeLimitedTimeOffer, markLimitedTimeOfferVisible, recordLimitedTimeOfferUpgradeApplied, settleLimitedTimeOfferUpgrade } from './limitedTimeOffer'

// Same box as the desktop Stack; phones tighten the padding and gap.
const OfferContent = styled('div', {
  display: 'flex',
  boxSizing: 'border-box',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '44px',
  padding: '40px 40px 28px 40px',
  background: lightTheme.colors.white,
  [COMPACT_MODAL_MEDIA]: { padding: '32px 16px 24px', gap: '28px' },
})

const SuccessContent = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: '100%',
  [COMPACT_MODAL_MEDIA]: { flexDirection: 'column' },
})

const PREMIUM_COLUMN = 'premium'
const SUPER_COLUMN = 'super'

interface OfferRowConfig {
  labelKey: string
  infoKey?: string
  /** `true`/`false` render as marks; numbers are formatted for the active locale. */
  values: Record<string, boolean | number>
}

/**
 * Rows of the Premium/Super comparison. Values mirror Figma 5774-13637 — the
 * plan entitlements are not derived from the user's profile, so they must be
 * kept in sync with the marketing copy in `metsikonPlanConfig`.
 */
const COMPARISON_ROWS: readonly OfferRowConfig[] = [
  {
    labelKey: 'Everything in Premium',
    values: { [PREMIUM_COLUMN]: true, [SUPER_COLUMN]: true },
  },
  {
    labelKey: 'AI credits',
    values: { [PREMIUM_COLUMN]: 250, [SUPER_COLUMN]: 1000 },
  },
  {
    labelKey: 'AI video',
    values: { [PREMIUM_COLUMN]: false, [SUPER_COLUMN]: true },
  },
  {
    labelKey: 'HD quality',
    values: { [PREMIUM_COLUMN]: false, [SUPER_COLUMN]: true },
  },
  {
    labelKey: 'Latest AI models',
    infoKey: 'Nano Banana, GPT image, etc.',
    values: { [PREMIUM_COLUMN]: false, [SUPER_COLUMN]: true },
  },
  {
    labelKey: 'Photos per batch (web)',
    values: { [PREMIUM_COLUMN]: 50, [SUPER_COLUMN]: 1000 },
  },
]

function ModalLimitedTimeOffer() {
  const { showModalType } = useModal()
  const { t, i18n } = useTranslation()

  const isOpen = showModalType === ShowModalType.LIMITED_TIME_OFFER

  // The offer adds no charge today; keep renewal pricing and its currency
  // tied to the existing Super monthly preview, with an immediate USD fallback.
  const [prices, setPrices] = useState(() => ({
    extraToday: '$0',
    regular: getFallbackSuperPlanDetail().month.price,
  }))
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [upgradeFailed, setUpgradeFailed] = useState(false)
  const [upgradeSucceeded, setUpgradeSucceeded] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const manager = SignInManager.getInstance()

    manager
      .getPlanPrices()
      .then(({ monthlySuper }) => {
        if (cancelled) return
        const monthlySuperPrice = monthlySuper?.recurring?.price?.gross
        if (!monthlySuperPrice) return
        const { currencyCode, countryCode } = monthlySuper.recurring
        const extraToday = formatPlanPrice(0, currencyCode, countryCode, 0)
        setPrices({ extraToday, regular: monthlySuperPrice })
      })
      .catch(() => {
        /* keep the env-seeded prices */
      })

    return () => {
      cancelled = true
    }
  }, [isOpen])

  // A re-open (debug entry point) must not inherit the previous attempt's state.
  useLayoutEffect(() => {
    if (!isOpen) return
    setIsUpgrading(false)
    setUpgradeFailed(false)
    setUpgradeSucceeded(false)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !markLimitedTimeOfferVisible()) return
    customAmplitude('Limited Time Offer Prompt', {
      Source: LIMITED_TIME_OFFER_SOURCE,
      Type: 'Super',
    })
  }, [isOpen])

  const handleDismiss = () => {
    if (isUpgrading) return
    if (closeLimitedTimeOffer(true)) customAmplitude('Limited Time Offer Dismissed', { Source: LIMITED_TIME_OFFER_SOURCE })
  }

  /**
   * Closes the congrats screen shown after a successful upgrade. The tutorial
   * hand-off the offer took over from Metsikon is released here, at the real end
   * of the flow — not when the plan change settled.
   */
  const handleSuccessClose = () => {
    closeLimitedTimeOffer(false)
  }

  /**
   * The user already has an active monthly Premium subscription, so the offer
   * is applied by switching that subscription's price in place (Paddle plan
   * change) instead of opening a second checkout. On success the modal swaps to
   * the Super congrats screen — the same one a Super checkout ends on — and the
   * post-purchase tutorial hand-off waits until that screen is closed.
   */
  const handleUpgrade = () => {
    if (isUpgrading) return
    const owner = beginLimitedTimeOfferUpgrade()
    if (!owner) { setUpgradeFailed(true); return }
    setUpgradeFailed(false)
    setIsUpgrading(true)
    customAmplitude('Limited Time Offer Upgrade', {
      Source: LIMITED_TIME_OFFER_SOURCE,
      Type: 'Super',
    })
    void SignInManager.getInstance().upgradeToSpecialOfferSuperMonthly(
      { Source: LIMITED_TIME_OFFER_SOURCE, Type: 'Upgrade to Super', Duration: 'Monthly' },
      (completed?: boolean) => {
        if (!settleLimitedTimeOfferUpgrade(owner, Boolean(completed))) return
        setIsUpgrading(false)
        if (!completed) {
          setUpgradeFailed(true)
          return
        }
        setUpgradeSucceeded(true)
      },
      () => recordLimitedTimeOfferUpgradeApplied(owner),
      owner.subscriptionId,
    )
  }

  const accent = lightTheme.colors.purpleVivid
  const superLabel = t('Super')

  // Counts are grouped for the active locale: "1,000" in en, "1.000" in de,
  // "1 000" in fr. Hardcoding the en form would print the wrong number elsewhere.
  const formatCount = (value: number) => new Intl.NumberFormat(i18n.language).format(value)

  const rows: PlanComparisonRow[] = COMPARISON_ROWS.map(row => ({
    label: t(row.labelKey),
    info: row.infoKey ? t(row.infoKey) : undefined,
    values: Object.fromEntries(
      Object.entries(row.values).map(([column, value]) => [
        column,
        typeof value === 'number' ? formatCount(value) : value,
      ]),
    ),
  }))

  // The congrats screen is the two-column 920x652 frame TryPremiumSuccess is
  // built for (ModalWrapper's defaults), not the narrow offer card, and it
  // carries its own close affordances.
  if (upgradeSucceeded) {
    return (
      <ModalWrapper isOpen={isOpen} onClose={handleSuccessClose} hasCloseButton={false} dismissOnEscapeOrBackdrop focusLock>
        <SuccessContent>
          <TryPremiumSuccess planType="super" close={handleSuccessClose} />
        </SuccessContent>
      </ModalWrapper>
    )
  }

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleDismiss}
      hasCloseButton={false}
      dismissOnEscapeOrBackdrop={!isUpgrading}
      focusLock
      width="505px"
      height="auto"
      borderRadius="32px"
    >
      <OfferContent>
        <Stack gap={24} align="center" fullWidth>
          <Badge text={t('Limited time offer')} mainStyle="purple" uppercase />

          <Stack gap={8} fullWidth>
            {/* The accented run repeats the headline variant so the span carries
                the weight explicitly instead of inheriting it — a nested span
                with only a color set can be re-weighted by ambient CSS. */}
            <Text
              variant="Header26bold"
              as="h2"
              align="center"
              highlight={{ token: superLabel, color: accent, variant: 'Header26bold' }}
            >
              {t('Try Super free for 30 days')}
            </Text>
            <Text variant="Regular18" align="center">
              {t('Unlock all Super benefits for a month at no extra cost.')}
            </Text>
          </Stack>

          {/* Gap absorbs the trimmed line boxes: they are now font-size tall
              rather than shorter than the glyphs, so the same optical spacing
              needs a smaller gap. */}
          <Stack gap={10} fullWidth>
            <Text
              variant="Bold22Tight"
              align="center"
              color={accent}
            >
              {t('%@ extra today', { arg: prices.extraToday })}
            </Text>
            <Text variant="Regular16Tight" align="center" color={lightTheme.colors.grayScale400}>
              {t('then %@/month', { arg: prices.regular })}
            </Text>
          </Stack>
        </Stack>

        <PlanComparisonTable
          title={t('What you get')}
          accentColor={accent}
          columns={[
            { key: PREMIUM_COLUMN, label: t('Premium') },
            { key: SUPER_COLUMN, label: superLabel, highlighted: true },
          ]}
          rows={rows}
        />

        <Stack gap={16} align="center" fullWidth>
          {upgradeFailed && (
            <Text variant="Table14regular" align="center" color={lightTheme.colors.text.text_warning}>
              {t('Something went wrong. Please try again.')}
            </Text>
          )}
          <DefaultButton
            label={isUpgrading ? t('Just a sec...') : t('Upgrade now')}
            size="large"
            type="super-gradient"
            fullWidth
            disabled={isUpgrading}
            onClick={handleUpgrade}
          />
          <DefaultButton
            label={t('No thanks, skip free Super')}
            size="standard"
            type="text"
            disabled={isUpgrading}
            onClick={handleDismiss}
          />
        </Stack>
      </OfferContent>
    </ModalWrapper>
  )
}

export default ModalLimitedTimeOffer
